import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import { selectClientId, selectClientIsGuest } from '../../../store/selectors/client'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { DelayedBotHeadSpinWithMessage, Text } from '../..'
import { SavedSearch } from '../SavedSearch/SavedSearch'
import { EmptyState } from '../EmptyState/EmptyState'
import { FadeOnMount } from '../../shared/Animations'
import { setActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../../types/navigation'
import styles from './SavedSearches.module.scss'
import { Badge, Button, Flex, Heading } from '@homebotapp/hb-react-component-catalog'
import history from '../../../history'
import { useGetSavedSearches } from '../../../hooks/listings/useGetSavedSearches'
import { useCreateClientEvent, useEffectOncePerSessionConditionally } from '../../../hooks'
import { ClientEventName, ClientEventSource } from '../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  head: {
    id: 'HomeSearch.listings.savedSearch.underMaintenance.head',
    defaultMessage: 'Saved Search is temporarily unavailable.'
  },
  back: {
    id: 'HomeSearch.savedSearches.backButton',
    defaultMessage: 'Back'
  },
  heading: {
    id: 'HomeSearch.savedSearches.heading',
    defaultMessage: 'Saved searches'
  },
  emptyStateCopy: {
    id: 'HomeSearch.savedSearches.emptyState.copy',
    defaultMessage: "You haven't saved any searches yet."
  },
  emptyStateCta: {
    id: 'HomeSearch.savedSearches.emptyState.cta',
    defaultMessage: 'Start a new search'
  },
  loading: {
    id: 'HomeSearch.savedSearches.loading',
    defaultMessage: 'Loading your saved searches...'
  },
  body: {
    id: 'HomeSearch.listings.savedSearch.underMaintenance.body',
    defaultMessage: 'This feature is currently under maintenance. Please check back later.'
  }
})

const SavedSearches = () => {
  const intl = useIntl()
  const clientId = useSelector(selectClientId) as string
  const customerId = useSelector(selectCustomerId)
  const dispatch = useDispatch()

  const clientIsGuest = useSelector(selectClientIsGuest)
  const isMediumScreen = useMediaQuery({
    query: `(min-width: ${styles.mediumBreak})`
  })
  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  const createSavedSearchViewEvent = () => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.View,
        eventSource: ClientEventSource.SavedSearches
      }
    })
  }

  useEffectOncePerSessionConditionally(createSavedSearchViewEvent, 'View_SavedSearches', true)

  useEffect(() => {
    // Set the active tab to home search when we are on the saved searches page.
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.SEARCH))
  }, [dispatch])

  const { isLoading, data, refetch, isRefetching } = useGetSavedSearches(
    {
      input: { clientId: clientId }
    },
    {
      enabled: !!clientId
    }
  )

  const hasSavedSearches = data?.savedSearches && data?.savedSearches?.length > 0

  if (!clientIsGuest && isLoading) {
    return (
      <DelayedBotHeadSpinWithMessage className={styles.loadingContainer} loading={isLoading}>
        {/* <FormattedMessage id='BotHeadSpinWithMessage.SavedSearches' defaultMessage='Loading your saved searches...' /> */}
        {intl.formatMessage(MSG.loading)}
      </DelayedBotHeadSpinWithMessage>
    )
  }

  return (
    customerId &&
    (!isLoading || clientIsGuest) && (
      <FadeOnMount>
        <div className={styles.savedSearchesWrapper}>
          <Flex alignItems='center' mb={5}>
            <Button onClick={() => history.goBack()}>{intl.formatMessage(MSG.back)}</Button>
            <Heading size='xl' mb={0} ml={4}>
              {intl.formatMessage(MSG.heading)}
            </Heading>
            {hasSavedSearches && (
              <Badge size='lg' ml={4} colorScheme='primary' variant='round-solid'>
                {data?.savedSearches?.length}
              </Badge>
            )}
          </Flex>
          {hasSavedSearches &&
            data?.savedSearches?.map(savedSearch => (
              <React.Fragment key={savedSearch?.id}>
                <SavedSearch savedSearch={savedSearch} />
                <hr className={styles.savedSearchesDivider} />
              </React.Fragment>
            ))}
          {!hasSavedSearches && (
            <EmptyState copy={intl.formatMessage(MSG.emptyStateCopy)} ctaCopy={intl.formatMessage(MSG.emptyStateCta)} />
          )}
        </div>
      </FadeOnMount>
    )
  )
}

export default SavedSearches
